import { useEffect, useRef, useState, type JSX } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, type AccessRequestRow, type PriorRequestSummary } from '../../api-client';
import { AuthLayout } from './AuthLayout';
import { RegistrarContact } from './RegistrarContact';

/**
 * How every instant on this page is displayed: Pacific/Auckland, always (House
 * Rules — a user-facing time is an NZ time, whatever clock the browser is set
 * to).
 */
const nzDateTime = new Intl.DateTimeFormat('en-NZ', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'Pacific/Auckland',
  timeZoneName: 'short',
});

/**
 * A D1 datetime column, as the reader should see it.
 *
 * EVERY timestamp on this panel comes from D1's `datetime('now')`, whose output
 * is `"YYYY-MM-DD HH:MM:SS"` — UTC, but with a SPACE and no `Z`, a spelling
 * `new Date()` parses as the browser's LOCAL time. In Auckland that printed
 * "Requested" as an instant ~12 hours EARLIER than the real one — a request
 * shown as half a day older than it is, on a page whose whole job is to let a
 * Senior Minister judge whether it is suspiciously fresh. The worker
 * states the format in a comment beside its own `age_hours` fix
 * (`routes/access.ts`), and this is the same fix, done once: the panel used to
 * carry two spellings side by side — two sites converting correctly and two not.
 *
 * Exported so a new site takes THIS one rather than hand-rolling a fifth.
 * Anything already carrying a timezone (an ISO instant) is passed through to
 * `Date` untouched; an unparseable value is returned verbatim, so a corrupt
 * column reads as corrupt rather than as "Invalid Date".
 */
export function formatD1DateTime(value: string | null | undefined): string {
  if (!value) return '';
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
    ? `${value.replace(' ', 'T')}Z`
    : value;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? value : nzDateTime.format(d);
}

/**
 * The one sentence for a link that can no longer do anything: every closed
 * request, and a token the worker has no row for (mistyped, or deleted by
 * retention 12 months on). There used to be five wordings — "no longer active",
 * "can no longer be used", "no longer valid", "no longer works", "may have
 * expired or been used" — and a reader who met two could not tell whether they
 * meant different things (Miranda r2 on cca-g1mx2, cca-9gk09).
 */
const DEAD_LINK = 'This approval link no longer works.';

// The page never prints the api client's own error message. That message is
// `<METHOD> <path> failed: HTTP n`, and the path carries this link's token — a
// bearer capability: whoever holds it can read the request (Lazo, LeC and
// Miranda r1 on cca-9gk09). Every failure gets a fixed sentence instead.

/** A read that failed in any way but "no such request": the link may still work. */
const SERVER_FAULT =
  'Something went wrong on our side. Your link may still work — try again in a minute.';

/** An approve refused because no one is signed in. */
const APPROVE_SIGN_IN = 'Sign in as the approver to approve this request.';

/**
 * An approve refused because the signed-in account is not an approver. Not
 * "only the Senior Minister": an approver the request was forwarded to can
 * approve too (LeC P2, cca-0x72d).
 */
const APPROVE_WRONG_ACCOUNT =
  "Only this parish's Senior Minister or an approver it was forwarded to can approve this request. You're signed in as a different account. The request stays pending.";

/** An approve that failed on the server, or never reached it. */
const APPROVE_SERVER_FAULT = 'Something went wrong on our side. Try again in a minute.';

/**
 * The approve call's answers that can mean the request CLOSED while the page
 * was open: no row (404), already decided or withdrawn (409
 * `already_resolved`), past its 7 days (410). The Senior Minister and the
 * Wardens hold the same link under escalation, so one of them can act while
 * another has the page open (Miranda r1 S1, cca-9gk09).
 */
const CLOSED_WHILE_OPEN = new Set([404, 409, 410]);

/**
 * What a failed approve says while the request is still pending: a fixed
 * sentence, never the client's message. A worker refusal that carries its own
 * sentence (`message`, e.g. "ask the Registrar to move or link it. The request
 * stays pending.", cca-0x72d) is shown as sent, except on a 5xx, whose body is
 * not written for a reader.
 */
function approveFailureSentence(err: unknown): string {
  if (!(err instanceof ApiError) || err.status >= 500) return APPROVE_SERVER_FAULT;
  if (err.status === 401) return APPROVE_SIGN_IN;
  if (err.status === 403) return APPROVE_WRONG_ACCOUNT;
  // `||`, not `??`: an empty `message` is no sentence at all.
  return err.serverMessage || APPROVE_SERVER_FAULT;
}

/**
 * Why the page shows no request:
 *  - `not_found` — the worker has no row for this token: a read answered 404,
 *    or an approve answered 404 and the re-read failed too
 *  - `server_fault` — the read failed any other way, or answered 200 with no
 *    request (a worker anomaly, not a dead link). The link may still work
 *  - `closed` — an approve answered 409 or 410, so the request has closed, and
 *    the re-read that would say how failed
 */
type PageFailure = 'not_found' | 'server_fault' | 'closed';

/**
 * Shown to the Senior Minister when they click the approval link in their email.
 * Renders the request details and asks for one explicit confirmation click
 * before the Worker flips the requester's Portal_Edit_Access flag.
 *
 * H2 social-engineering protections layered on top:
 *  - Yellow out-of-band-verification banner above the request details
 *  - Prior-requests list from the same email so patterns (repeated denials,
 *    cross-parish fishing) surface before approval
 *  - 24h cooling-off: if the request is younger than 24h the Approve button
 *    is gated behind an "I verified out-of-band" checkbox
 *
 * Approving never links the request to an existing Contact: that was removed
 * (registrar, 21 Oct 2026) and is the Registrar's job. A possible existing
 * record is shown as a plain note, with no id, name or address (cca-0x72d).
 *
 * When there is no request to show, the page says why in fixed words, never
 * the client's error message (see {@link PageFailure}): a link with no request
 * behind it is a closed-style panel, and a fault offers "Try again". An approve
 * that finds the request closed re-reads it and shows how it ended, as a fresh
 * load of the link would. Each panel that replaces the page takes focus on its
 * heading ({@link EndHeading}).
 */
export function ApprovalPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [request, setRequest] = useState<AccessRequestRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<PageFailure | null>(null);
  // Bumped by "Try again", which re-runs the read below.
  const [attempt, setAttempt] = useState(0);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [approved, setApproved] = useState(false);
  const [verifiedOutOfBand, setVerifiedOutOfBand] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .getAccessRequest(token)
      .then((r) => {
        if (r) setRequest(r);
        else setFailure('server_fault');
      })
      // A 404 is a token the worker has no row for: a dead link. Any other
      // failure is a fault, and the link may still work.
      .catch((err: unknown) =>
        setFailure(err instanceof ApiError && err.status === 404 ? 'not_found' : 'server_fault'),
      )
      .finally(() => setLoading(false));
  }, [token, attempt]);

  const retry = (): void => {
    setFailure(null);
    setLoading(true);
    setAttempt((n) => n + 1);
  };

  const approve = async (): Promise<void> => {
    if (!token) return;
    setApproving(true);
    setApproveError(null);
    try {
      await api.approveAccessRequest(token);
      setApproved(true);
    } catch (err) {
      if (err instanceof ApiError && CLOSED_WHILE_OPEN.has(err.status)) {
        // Another approver acted, the requester withdrew, or the 7 days ran
        // out while the page was open. Read the request again and show how it
        // ended, so the page is never left pending with a live Approve button.
        const reread = await api.getAccessRequest(token).catch(() => null);
        if (reread && reread.status !== 'pending') {
          setRequest(reread);
          return;
        }
        if (!reread) {
          setFailure(err.status === 404 ? 'not_found' : 'closed');
          return;
        }
        // Still pending on the re-read: a refusal, not a closure. Said below.
      }
      setApproveError(approveFailureSentence(err));
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout>
        <Centered>
          <p role="status">Loading request…</p>
        </Centered>
      </AuthLayout>
    );
  }

  if (failure === 'not_found') {
    return (
      <AuthLayout>
        <NotFoundPanel />
      </AuthLayout>
    );
  }

  if (failure === 'closed' && request) {
    // How it closed is unknown, so the neutral message, for the parish the
    // page was already showing.
    return (
      <AuthLayout>
        <ResolvedRequestPanel parishName={request.parish_name} status={null} resolvedAt={null} />
      </AuthLayout>
    );
  }

  if (failure === 'server_fault' || !request) {
    return (
      <AuthLayout>
        <ServerFaultPanel onRetry={retry} />
      </AuthLayout>
    );
  }

  if (request.status !== 'pending') {
    return (
      <AuthLayout>
        <ResolvedRequestPanel
          parishName={request.parish_name}
          status={request.status}
          resolvedAt={request.resolved_at}
        />
      </AuthLayout>
    );
  }

  if (approved) {
    return (
      <AuthLayout>
        <div className="panel">
          <div className="panel-section">
            <EndHeading className="text-xl font-semibold text-ccaanz-green">Approved</EndHeading>
            <p className="mt-2 text-sm text-ccaanz-black">
              <strong>{request.requester_email}</strong> can now edit{' '}
              <strong>{request.parish_name}</strong>'s personnel records. They've been emailed with
              confirmation.
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  const isFresh = request.age_hours < 24;
  const canApprove = !isFresh || verifiedOutOfBand;
  const priorOther = request.prior_requests.filter((r) => !r.is_this_one);

  return (
    <AuthLayout>
      <div className="space-y-6">
        <header>
          <h2 className="text-2xl font-semibold text-ccaanz-black">Approve access request</h2>
          <p className="mt-2 text-sm text-ccaanz-grey">
            You've been asked to approve someone to edit{' '}
            <strong className="text-ccaanz-black">{request.parish_name}</strong>'s personnel
            records.
          </p>
        </header>

        <div className="rounded-control border border-ccaanz-amber bg-ccaanz-amber-light px-4 py-3 text-sm text-ccaanz-amber-dark">
          <p className="font-semibold">Verify this person out-of-band before approving.</p>
          <p className="mt-1">
            Anyone with a verified Google account can reach this page after submitting a request. A
            phisher could use a familiar-looking name and a plausible reason to slip through. Phone
            or message the requester via a number/channel you already have for them — not the email
            shown below — before granting access.
          </p>
        </div>

        <div className="panel">
          <div className="panel-section space-y-3">
            <DetailRow label="Requester">{request.requester_email}</DetailRow>
            <DetailRow label="Parish">{request.parish_name}</DetailRow>
            <DetailRow label="Reason">
              <span className="whitespace-pre-wrap">{request.reason}</span>
            </DetailRow>
            <DetailRow label="Requested">
              {formatD1DateTime(request.created_at)}
              <span className="ml-2 text-xs text-ccaanz-grey">
                ({formatAge(request.age_hours)})
              </span>
            </DetailRow>
          </div>
        </div>

        {request.escalated_at && (
          <div className="rounded-control border border-ccaanz-blue bg-ccaanz-blue-light px-4 py-3 text-sm text-ccaanz-blue-dark">
            <p className="font-semibold">This request has been escalated.</p>
            <p className="mt-1">
              The Senior Minister hadn't acted within 24 hours, so this request was also forwarded
              to the parish Wardens (or the registrar) on{' '}
              <strong>{formatD1DateTime(request.escalated_at)}</strong>. Any of them can act on it —
              the registrar has been copied for diocese-wide visibility.
            </p>
            {request.viewer_is_escalated_approver && (
              // No grey of its own: it inherits the banner's blue-dark, 9.50:1
              // on the blue tint where the grey was 4.11:1 (Miranda r1, cca-0x72d).
              <p className="mt-1 text-xs">You are one of the people it was forwarded to.</p>
            )}
          </div>
        )}

        <PriorRequestsPanel priors={priorOther} />

        {request.similar_name_at_parish && <SimilarNameNote parishName={request.parish_name} />}

        {isFresh && (
          <div className="panel">
            <div className="panel-section">
              <p className="text-sm font-semibold text-ccaanz-black">
                Cooling-off — this request is less than 24 hours old.
              </p>
              <p className="mt-1 text-xs text-ccaanz-grey">
                Confirm you've reached the requester via a channel you trust before the Approve
                button enables.
              </p>
              <label className="mt-3 flex items-start gap-2 text-sm text-ccaanz-black">
                <input
                  type="checkbox"
                  checked={verifiedOutOfBand}
                  onChange={(e) => setVerifiedOutOfBand(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I have verified this request with <strong>{request.requester_email}</strong>{' '}
                  out-of-band (phone, in person, or a channel I already had for them).
                </span>
              </label>
            </div>
          </div>
        )}

        {approveError && (
          // role="alert": a refusal is announced the moment it appears, wherever
          // the reader's focus is. Several refusals say "ask the Registrar", so
          // the route to them is inside the box.
          <div
            role="alert"
            className="rounded-control border border-ccaanz-red bg-ccaanz-red-light px-3 py-2 text-sm text-ccaanz-red"
          >
            <p>{approveError}</p>
            <RegistrarContact className="mt-1" />
          </div>
        )}

        <div className="panel">
          <div className="panel-section flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-ccaanz-grey sm:max-w-md">
              Approving will grant edit access and email the requester to let them know they're in.
              If you don't recognise this person, simply close this page — the request stays
              pending.
            </p>
            <button
              type="button"
              disabled={approving || !canApprove}
              onClick={approve}
              className="btn btn-primary self-end sm:self-auto"
            >
              {approving ? 'Approving…' : 'Approve access'}
            </button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}

function PriorRequestsPanel({ priors }: { priors: PriorRequestSummary[] }): JSX.Element {
  if (priors.length === 0) {
    return (
      <div className="panel">
        <div className="panel-section">
          <p className="text-xs font-semibold uppercase tracking-wide text-ccaanz-grey">
            Prior requests from this email
          </p>
          <p className="mt-2 text-sm text-ccaanz-black">
            None — this is their first access request.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="panel">
      <div className="panel-section">
        <p className="text-xs font-semibold uppercase tracking-wide text-ccaanz-grey">
          Prior requests from this email
        </p>
        <ul className="mt-2 space-y-1.5 text-sm text-ccaanz-black">
          {priors.map((p, i) => (
            <li key={i} className="flex items-baseline gap-2">
              <span className={statusToneClass(p.status)}>{p.status}</span>
              <span>{p.parish_name}</span>
              <span className="text-xs text-ccaanz-grey">{formatD1DateTime(p.created_at)}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-ccaanz-grey">
          Repeated denials across parishes, or rapid fishing across multiple parishes, is a red
          flag.
        </p>
      </div>
    </div>
  );
}

/**
 * Someone with a similar name may already be on file at this parish. A notice,
 * not a choice: the worker serves only a yes/no — no id, name or address — and
 * linking the two records is the Registrar's job (registrar, 21 Oct 2026). It
 * leads with what to do, warns that a name match vouches for nobody (the
 * out-of-band check above still stands), and carries the Registrar's address.
 */
function SimilarNameNote({ parishName }: { parishName: string }): JSX.Element {
  return (
    <div className="panel">
      <div className="panel-section">
        <p className="text-xs font-semibold uppercase tracking-wide text-ccaanz-grey">
          Possible existing record
        </p>
        <p className="mt-2 text-sm text-ccaanz-black">
          Someone with a similar name may already be on file at <strong>{parishName}</strong>. You
          can still approve. If it is the same person, let the Registrar know and they will link the
          two records. A matching name is not proof it is them — verify them as above.
        </p>
        <RegistrarContact className="mt-2 text-sm text-ccaanz-black" />
      </div>
    </div>
  );
}

function statusToneClass(status: string): string {
  if (status === 'approved')
    return 'inline-block min-w-[5rem] text-xs font-semibold uppercase text-ccaanz-green';
  if (status === 'denied')
    return 'inline-block min-w-[5rem] text-xs font-semibold uppercase text-ccaanz-red';
  if (status === 'expired')
    return 'inline-block min-w-[5rem] text-xs font-semibold uppercase text-ccaanz-grey';
  return 'inline-block min-w-[5rem] text-xs font-semibold uppercase text-ccaanz-grey';
}

function formatAge(hours: number): string {
  if (hours < 1) return 'just now';
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function Centered({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="mx-auto max-w-md py-12 text-center text-sm text-ccaanz-grey">{children}</div>
  );
}

/**
 * The heading of a panel that has just REPLACED the page (closed, Approved,
 * not found, server fault): focused once, on mount, and focusable without
 * joining the tab order (`tabIndex={-1}`). The control the reader was on —
 * Approve, or Try again — has unmounted, so without the move the keyboard
 * drops to `<body>`; the focused heading is also what a screen reader reads
 * out (Miranda r1 S4, cca-9gk09). The focus-on-mount pattern UX-10 points to
 * (cca-decides-web's `setup/BallotBuilder.tsx` and its `useFocusOnMount`),
 * written here because one web package does not import another's internals.
 */
function EndHeading({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}): JSX.Element {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
    // Mount-only: a re-render never pulls focus back from where the reader moved it.
  }, []);
  return (
    <h2 ref={heading} tabIndex={-1} className={className}>
      {children}
    </h2>
  );
}

/**
 * A link the worker has no request for: mistyped, cut short in the copying, or
 * deleted by retention 12 months on. Not a fault, so a closed-style panel in
 * grey — what happened, the one dead-link sentence, the next step — not the
 * red title of {@link ServerFaultPanel} (Miranda r1 S2, cca-9gk09).
 */
function NotFoundPanel(): JSX.Element {
  return (
    <div className="panel">
      <div className="panel-section space-y-3">
        <EndHeading className="text-xl font-semibold text-ccaanz-grey">
          Request not found
        </EndHeading>
        <p className="text-sm text-ccaanz-black">
          We can't find a request for this link. It may have been copied only in part, or be more
          than a year old. {DEAD_LINK} If someone still needs access, ask them to send a new
          request.
        </p>
        <RegistrarContact />
      </div>
    </div>
  );
}

/**
 * The read failed on the server, never reached it, or came back with no
 * request. The link may still work, so this panel says so, never calls it dead,
 * and offers "Try again" (UX-7's error-with-retry), which re-runs the read
 * (Miranda r1 S3, cca-9gk09).
 */
function ServerFaultPanel({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <div className="panel">
      <div className="panel-section space-y-3">
        <EndHeading className="text-xl font-semibold text-ccaanz-red">
          Could not load this request.
        </EndHeading>
        <p className="text-sm text-ccaanz-black">{SERVER_FAULT}</p>
        <button type="button" onClick={onRetry} className="btn btn-primary">
          Try again
        </button>
        <RegistrarContact />
      </div>
    </div>
  );
}

/** The four closed statuses the shared type names. */
type ClosedStatus = Exclude<AccessRequestRow['status'], 'pending'>;

/**
 * A request that is no longer pending. The worker serves a closed request
 * without the requester's address, reason or history (cca-g1mx2) — the link
 * can outlive the decision — so this copy says "the requester" and names
 * nobody. It takes the parish, the status and the time as props, never the
 * row, so it cannot reach anything else the row carries. Every message names
 * the parish, as the pending page does, so the reader knows which request the
 * link was for, and says the link is dead in the one wording
 * ({@link DEAD_LINK}), then the next step.
 *
 * 'Approved' tells the PAST EVENT and its date, never present access: revoking
 * someone's access flips their Contact's flag and leaves this row 'approved',
 * so an old link cannot know whether the requester can still edit (Miranda r2
 * on cca-g1mx2, cca-9gk09). A decline and a withdrawal carry their date in the
 * sentence too; expiry has none, as no one decided it (Miranda r1 P1, cca-9gk09).
 *
 * The shared type lists four closed statuses, but the worker serves ANY status
 * that is not 'pending' as closed and D1's `status` column has no CHECK. So a
 * status this page does not know — or `null`, a request an approve found
 * closed without saying how — gets a neutral "closed" message rather than a
 * blank page, with a "Closed on" line when there is a time. `Object.hasOwn`
 * keeps a status like `toString` from matching an inherited property.
 */
function ResolvedRequestPanel({
  parishName,
  status,
  resolvedAt,
}: {
  parishName: string;
  status: string | null;
  resolvedAt: string | null;
}): JSX.Element {
  const resolved = formatD1DateTime(resolvedAt);
  const on = resolved ? ` on ${resolved}` : '';
  const parish = <strong>{parishName}</strong>;

  const known: Record<ClosedStatus, { heading: string; tone: string; body: JSX.Element }> = {
    approved: {
      heading: 'Already approved',
      tone: 'text-ccaanz-green',
      body: (
        <>
          This request for access at {parish} was approved{on}. {DEAD_LINK} If the requester needs
          access again, ask them to submit a new request.
        </>
      ),
    },
    denied: {
      heading: 'Already declined',
      tone: 'text-ccaanz-red',
      body: (
        <>
          This request for access at {parish} was declined{on}. {DEAD_LINK} If you'd like to grant
          the requester access, ask them to submit a new request.
        </>
      ),
    },
    expired: {
      heading: 'Request expired',
      tone: 'text-ccaanz-grey',
      body: (
        <>
          This request wasn't approved within 7 days, so it expired. {DEAD_LINK} If the requester
          still needs access at {parish}, ask them to submit a new request.
        </>
      ),
    },
    cancelled: {
      heading: 'Request withdrawn',
      tone: 'text-ccaanz-grey',
      body: (
        <>
          The requester withdrew their request for access at {parish}
          {on}. {DEAD_LINK} No action is needed.
        </>
      ),
    },
  };
  const unknownStatus = {
    heading: 'This request is closed',
    tone: 'text-ccaanz-grey',
    body: (
      <>
        {DEAD_LINK} If the requester still needs access at {parish}, ask them to submit a new
        request.
      </>
    ),
  };
  const isKnown = (s: string | null): s is ClosedStatus => s !== null && Object.hasOwn(known, s);
  const copy = isKnown(status) ? known[status] : unknownStatus;

  return (
    <div className="panel">
      <div className="panel-section space-y-3">
        {/* text-xl, as the Approved confirmation's heading is (Miranda r2, cca-9gk09). */}
        <EndHeading className={`text-xl font-semibold ${copy.tone}`}>{copy.heading}</EndHeading>
        <p className="text-sm text-ccaanz-black">{copy.body}</p>
        {!isKnown(status) && resolved && (
          <p className="text-xs text-ccaanz-grey">Closed on {resolved}.</p>
        )}
        <RegistrarContact />
      </div>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <p className="input-label">{label}</p>
      <p className="mt-0.5 text-sm text-ccaanz-black">{children}</p>
    </div>
  );
}
